import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, computed, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GridColumn, GridConfig } from '@app/features/v3ndor/core/models/grid.model';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { SwitchThemeService } from '@app/shared/components/switch-theme/switch-theme.service';
import { PasswordConfirm } from '@app/shared/components/form/validators';
import { ToastService } from '@teorema/patron';
import {
  BulkMaintenanceBwUser,
  BwUserBulkResult,
} from 'src/app/models/bw-user-bulk-maintenance.model';
import {
  AccessType,
  AccessTypeProfile,
  BW_USER_INACTIVE_REASON_LABEL,
  BwUser,
  bwUserActiveFilter,
} from 'src/app/models/bw-user.model';
import { System } from 'src/app/models/core-config.model';
import { FilterField } from 'src/app/models/filter-field.model';
import { FilterOperator, FindOptions } from 'src/app/models/find-options.model';
import { BwUserService, PanelConfigurationService, ProfileService, utils } from 'src/app/shared';
import { WebLicenseCustomerService } from 'src/app/shared/services/web-license-customer.service';
import { fetchRemainingPages } from 'src/app/shared/utils';
import { WebLicense } from 'src/app/models/web-license.model';
import { AuthService } from '../../auth.service';

/** Linha achatada que o app-advanced-grid consome (nunca o model aninhado). */
type BwUserRow = Record<string, unknown> & { id: string };

/** Uma página grande basta para o volume de acessos de um grupo empresa. */
const PAGE_SIZE = 5000;

@Component({
  selector: 'app-bw-user-list',
  templateUrl: './bw-user-list.component.html',
  styleUrl: './bw-user-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
  standalone: false,
})
export class BwUserListComponent implements OnInit, OnDestroy {
  bwUsers: BwUser[] = [];
  rows: BwUserRow[] = [];
  loading = false;
  newUserModalVisible = false;
  saving = false;
  userCanWrite = false;
  newUserFormGroup: FormGroup;
  changePasswordFormGroup: FormGroup;

  // Tema claro/escuro do app-advanced-grid: mesmo padrão das telas do features/v3ndor e
  // do modules/support, para expor as variáveis CSS de badge.
  private switchThemeService = inject(SwitchThemeService);
  private themeSignal = toSignal(this.switchThemeService.currentTheme, { initialValue: 'light' });
  currentThemeClass = computed(() =>
    this.themeSignal() === 'dark' ? 'tw-dark-theme' : 'tw-light-theme'
  );

  /**
   * Busca, ordenação e filtro passam a ser do app-advanced-grid (client-side, no Web
   * Worker), como em /v3ndor/clientes: carregamos todos os acessos de uma vez e o grid
   * cuida do resto. Os filtros que antes eram server-side (ativo, tipo de cadastro,
   * perfil, último acesso) viraram filtros de coluna.
   */
  findOptions: FindOptions = {
    perPage: PAGE_SIZE,
    orderBy: 'name',
    withs: ['bw_user_profiles'],
  };

  /**
   * A listagem sempre mostrou só os acessos ativos por padrão. Com o grid client-side isso
   * continua no servidor — carregar inativos por padrão mudaria o que a tela significa —, e o
   * botão da toolbar é a forma explícita de incluí-los. Desde o chamado 0000581710 o filtro é o
   * status efetivo (bwUserActiveFilter), que também considera o vínculo com o grupo e o
   * usuário do ERP, e não mais só `bw_users.active`.
   */
  showInactive = false;

  gridConfig: GridConfig = {
    resizableColumns: true,
    reorderableColumns: true,
    stripedRows: true,
    virtualScroll: true,
    virtualScrollItemSize: 40,
    scrollHeight: 'flex',
    sortMode: 'multiple',
  };

  profilesFindOptions: FindOptions = {
    orderBy: 'name',
  };
  profilesAddItemProperties = [
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.User),
      key_compare: 'type',
      value_compare: AccessTypeProfile.User,
    },
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.Customer),
      key_compare: 'type',
      value_compare: AccessTypeProfile.Customer,
    },
    {
      key: 'color',
      value: utils.getAccessTypeColor(AccessType.Supplier),
      key_compare: 'type',
      value_compare: AccessTypeProfile.Supplier,
    },
  ];

  panelConfig$ = this.panelConfigService.panelConfig$;

  patronUtils = utils;

  // ─── Seleção e manutenção em massa ───────────────────────────────────────
  selectedBwUserIds: string[] = [];
  bulkMaintenanceVisible = false;

  constructor(
    private service: BwUserService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private panelConfigService: PanelConfigurationService,
    private layoutService: LayoutService,
    private datePipe: DatePipe,
    private licenseService: WebLicenseCustomerService,
    public profileService: ProfileService
  ) {
    this.newUserFormGroup = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.email]],
        name: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirm: ['', [Validators.required, Validators.minLength(6)]],
        bw_user_profiles: [null],
        web_license_id: [null],
      },
      {
        validators: [PasswordConfirm('password', 'confirm')],
      }
    );

    this.changePasswordFormGroup = this.formBuilder.group(
      {
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirm: ['', [Validators.required, Validators.minLength(6)]],
      },
      {
        validators: [PasswordConfirm('password', 'confirm')],
      }
    );
  }

  columns: GridColumn[] = [];

  ngOnInit() {
    // Só escrita, sem exigir execução: é o que o backend cobra (BwUserPolicy::update →
    // checkAccess(true)). Exigir execução aqui esconderia o botão de quem a API aceita.
    this.userCanWrite = utils.checkAccess(['Users'], true, true).result;
    this.canManagePassword = this.checkCanManagePassword();
    this.columns = this.buildColumns();

    this.layoutService.setPageTitle('Usuários');
    // Telas com grid de altura travada zeram o padding-bottom do layout — sem isso os
    // 8rem reservados para o footer sobram no fim e criam o scroll fantasma.
    this.layoutService.setPadding('bottom', '0px');

    this.findBwUsers();
  }

  ngOnDestroy(): void {
    this.layoutService.setPageTitle('');
    this.layoutService.resetPadding('bottom');
  }

  // ─── Colunas ─────────────────────────────────────────────────────────────

  private buildColumns(): GridColumn[] {
    const columns: GridColumn[] = [
      { field: 'name', header: 'Nome', sortable: true, filterable: true },
      {
        field: 'access_types',
        header: 'Tipo',
        width: '170px',
        sortable: true,
        filterable: true,
        filterType: 'dropdown',
        filterSplit: ', ',
        htmlFormat: (_value, row) => String(row['access_types_html'] ?? ''),
      },
      { field: 'email', header: 'Email', sortable: true, filterable: true },
      { field: 'login', header: 'Login', sortable: true, filterable: true },
      {
        field: 'profiles_label',
        header: 'Perfis',
        sortable: true,
        filterable: true,
        filterType: 'dropdown',
        filterSplit: ', ',
        htmlFormat: (_value, row) => String(row['profiles_html'] ?? ''),
      },
      {
        field: 'last_access',
        header: 'Acessado',
        width: '160px',
        align: 'center',
        sortable: true,
        format: value =>
          value ? this.datePipe.transform(value as string, 'dd/MM/yyyy HH:mm') || '' : 'Nunca',
      },
      {
        field: 'active_label',
        header: 'Ativo',
        width: '110px',
        align: 'center',
        sortable: true,
        filterable: true,
        filterType: 'dropdown',
        htmlFormat: (_value, row) => this.formatActiveBadge(row),
      },
    ];

    if (this.canManagePassword) {
      columns.push(
        {
          field: 'change_password_action',
          header: '',
          width: '60px',
          align: 'center',
          actionType: 'change-password',
          actionIcon: 'pi pi-key',
          actionTooltip: 'Alterar senha',
        },
        {
          field: 'reset_link_action',
          header: '',
          width: '60px',
          align: 'center',
          actionType: 'reset-link',
          actionIcon: 'pi pi-link',
          actionTooltip: 'Gerar link de redefinição de senha',
        }
      );
    }

    return columns;
  }

  /** O grid consome dicionários planos; o model aninhado fica em `bwUsers`. */
  private flattenBwUserRow(bwUser: BwUser): BwUserRow {
    const profiles = utils.getProfilesThisCompanyGroup(bwUser.bw_user_profiles);

    const accessTypes: string[] = [];
    if (bwUser.is_user) accessTypes.push(utils.getAccessTypeLabel('user'));
    if (bwUser.is_customer) accessTypes.push(utils.getAccessTypeLabel('customer'));
    if (bwUser.is_supplier) accessTypes.push(utils.getAccessTypeLabel('supplier'));

    return {
      id: bwUser.id!,
      name: bwUser.name ?? '',
      email: bwUser.email ?? '',
      login: bwUser.login ?? '',
      access_types: accessTypes.join(', '),
      profiles_label: profiles.map(profile => profile.name).join(', '),
      last_access: bwUser.config?.last_access ?? null,
      active_label: bwUser.active ? 'Sim' : 'Não',
      // Chamado 0000581710 — o motivo aparece no tooltip do badge "Não".
      inactive_reason_label: bwUser.inactive_reason
        ? BW_USER_INACTIVE_REASON_LABEL[bwUser.inactive_reason]
        : '',
      is_user: !!bwUser.is_user,
      is_customer: !!bwUser.is_customer,
      is_supplier: !!bwUser.is_supplier,
      // O HTML dos badges é montado uma vez, na carga: htmlFormat é chamado a cada
      // render/exportação e não deve ir procurar o model aninhado a cada célula.
      access_types_html: this.buildAccessTypeBadges(bwUser),
      profiles_html: this.buildProfileBadges(profiles),
    };
  }

  private buildAccessTypeBadges(bwUser: BwUser): string {
    const badges: string[] = [];
    const push = (type: string) => {
      const variant = this.colorToVariant(utils.getAccessTypeColor(type));
      badges.push(
        `<span class="status-badge status-badge--${variant}">${this.escapeHtml(
          utils.getAccessTypeLabel(type)
        )}</span>`
      );
    };

    if (bwUser.is_user) push('user');
    if (bwUser.is_customer) push('customer');
    if (bwUser.is_supplier) push('supplier');

    return badges.join(' ');
  }

  private buildProfileBadges(profiles: { name?: string; type?: string }[]): string {
    return profiles
      .map(profile => {
        const accessType = utils.getAccessTypeByProfileType(profile?.type ?? '');
        const variant = this.colorToVariant(utils.getAccessTypeColor(accessType));
        return `<span class="status-badge status-badge--${variant}">${this.escapeHtml(
          profile.name ?? ''
        )}</span>`;
      })
      .join(' ');
  }

  private formatActiveBadge(row: Record<string, unknown>): string {
    const active = row['active_label'] === 'Sim';
    const variant = active ? 'open' : 'cancelled';
    const reason = String(row['inactive_reason_label'] ?? '');
    const title = !active && reason ? ` title="${this.escapeHtml(reason)}"` : '';
    return `<span class="status-badge status-badge--${variant}"${title}>${active ? 'Sim' : 'Não'}</span>`;
  }

  private colorToVariant(color: string): string {
    switch (color) {
      case 'primary':
      case 'info':
        return 'open';
      case 'success':
        return 'closed';
      case 'warning':
        return 'waiting';
      case 'danger':
        return 'cancelled';
      case 'secondary':
      case 'dark':
        return 'closed';
      default:
        return 'default';
    }
  }

  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  // ─── Carregamento ────────────────────────────────────────────────────────

  toggleInactive() {
    this.showInactive = !this.showInactive;
    this.findBwUsers();
  }

  private buildFindOptions(page?: number): FindOptions {
    return {
      ...this.findOptions,
      page,
      filters: this.showInactive ? [] : [bwUserActiveFilter()],
    };
  }

  async findBwUsers() {
    this.loading = true;

    // findAllBasic → bw-users/basic: o endpoint padrão paga dois bcrypt por usuário (o append
    // change_password do BwUser).
    const response = await this.service.findAllBasic(this.buildFindOptions()).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      return null;
    });

    this.bwUsers = await fetchRemainingPages(
      response?.data ?? [],
      response?.meta?.total ?? response?.data?.length ?? 0,
      page => this.service.findAllBasic(this.buildFindOptions(page))
    );

    this.rows = this.bwUsers.map(bwUser => this.flattenBwUserRow(bwUser));
    this.pruneSelection();
    this.loading = false;
  }

  // ─── Interação com o grid ────────────────────────────────────────────────

  onRowClicked(row: unknown) {
    const id = (row as BwUserRow)?.id;
    if (id) this.router.navigate([`/usuarios/${id}`]);
  }

  onActionClick(event: { row: unknown; action: string }) {
    const bwUser = this.bwUsers.find(item => item.id === (event.row as BwUserRow)?.id);
    if (!bwUser) return;

    if (event.action === 'change-password') this.openChangePasswordModal(bwUser);
    if (event.action === 'reset-link') this.generatePasswordResetLink(bwUser);
  }

  onSelectionChange(ids: string[]) {
    this.selectedBwUserIds = ids;
  }

  /** Descarta da seleção ids que não estão mais na listagem carregada. */
  private pruneSelection() {
    if (!this.selectedBwUserIds.length) return;
    const present = new Set(this.rows.map(row => row.id));
    this.selectedBwUserIds = this.selectedBwUserIds.filter(id => present.has(id));
  }

  // ─── Manutenção em massa ─────────────────────────────────────────────────

  get selectedForBulkMaintenance(): BulkMaintenanceBwUser[] {
    const selected = new Set(this.selectedBwUserIds);

    return this.bwUsers
      .filter(bwUser => !!bwUser.id && selected.has(bwUser.id))
      .map(bwUser => ({
        id: bwUser.id!,
        name: bwUser.name,
        email: bwUser.email,
        login: bwUser.login,
        is_user: !!bwUser.is_user,
        is_customer: !!bwUser.is_customer,
        is_supplier: !!bwUser.is_supplier,
        profiles: utils.getProfilesThisCompanyGroup(bwUser.bw_user_profiles),
      }));
  }

  openBulkMaintenance() {
    if (!this.selectedBwUserIds.length) return;

    this.bulkMaintenanceVisible = false;
    setTimeout(() => {
      this.bulkMaintenanceVisible = true;
    });
  }

  onBulkMaintenanceRemove(id: string) {
    this.selectedBwUserIds = this.selectedBwUserIds.filter(selected => selected !== id);
  }

  async onBulkMaintenanceDone(result: BwUserBulkResult) {
    // Falha parcial: a seleção fica só com os que falharam, porque nem toda escrita do
    // lote é idempotente — reaplicar em todos poderia duplicar efeito.
    this.selectedBwUserIds = result.failed
      ? result.results.filter(item => !item.ok).map(item => item.id)
      : [];

    await this.findBwUsers();
  }

  // ─── Criar usuário ───────────────────────────────────────────────────────

  async openNewUserForm() {
    const filter = new FilterField({
      label: 'Tipo',
      name: 'type',
      type: 'text',
      multiple: true,
      operator: FilterOperator.in,
      value: ['U'],
    });
    this.profilesFindOptions.filters = [filter];

    this.newUserModalVisible = false;
    setTimeout(() => {
      this.newUserModalVisible = true;
    });

    this.newUserFormGroup.patchValue({ web_license_id: null });
    this.availableLicenses = [];

    // Chamado #0000577675 — revalida `control_licenses` aqui, não em polling: é o único momento
    // desta tela em que o valor importa, então só paga o custo de rede quem realmente for usar.
    await this.authService.refreshCurrentCompanyGroup();
    if (this.isLicenseControlActive) {
      this.loadAvailableLicenses();
    }
  }

  /** Chamado #0000577675 — assentos livres do grupo; falha não trava o modal (só toast de erro). */
  availableLicenses: WebLicense[] = [];
  loadingLicenses = false;

  async loadAvailableLicenses() {
    this.loadingLicenses = true;
    try {
      const response = await this.licenseService.list({ only_available: true });
      this.availableLicenses = response?.data ?? [];

      // Chamado #0000577675 — sobra assento de um único sistema: pré-seleciona (escolha óbvia).
      const systemIds = new Set(this.availableLicenses.map(l => l.vendor_system_id));
      if (this.canLinkLicense && this.availableLicenses.length && systemIds.size === 1) {
        this.newUserFormGroup.patchValue({ web_license_id: this.availableLicenses[0].id });
      }
    } catch (error) {
      this.availableLicenses = [];
      this.toastService.show({
        color: 'error',
        message: 'Houve um erro ao carregar os assentos de licença disponíveis',
      });
    } finally {
      this.loadingLicenses = false;
    }
  }

  /** Chamado #0000577675 — grupo com controle de licenças ativo: sem assento, o usuário criado
   * fica sem acesso ao sistema (ver utils.hasNoLicenseInCurrentGroup(), mesmo aviso pro logado). */
  get isLicenseControlActive(): boolean {
    return this.currentCompanyGroup?.control_licenses === true;
  }

  /** Aviso some enquanto houver assento selecionado (ex.: pré-selecionado); volta se desmarcar. */
  get showNoLicenseWarning(): boolean {
    return this.isLicenseControlActive && !this.newUserFormGroup.get('web_license_id')?.value;
  }

  get canLinkLicense(): boolean {
    return this.isLicenseControlActive && this.licenseService.canWrite();
  }

  get isEnableNewUser() {
    return (
      this.authService.checkSystem([System.PRAXIS]) &&
      utils.isUser() &&
      utils.checkAccess(['Users'], true, true).result
    );
  }

  currentBwUser: any = undefined;
  currentCompanyGroup = utils.getCompanyGroup();
  loadingBwUser: boolean = false;

  async findByEmail(email: string) {
    if (!email || !email.length) return;
    if (this.newUserFormGroup.get('email')?.invalid) return;

    this.loadingBwUser = true;
    this.currentBwUser = undefined;
    this.newUserFormGroup.patchValue({
      name: '',
      bw_user_profiles: null,
      password: '',
      confirm: '',
    });

    const response: any = await this.service
      .findByEmail(email)
      .finally(() => (this.loadingBwUser = false));
    if (response?.id) {
      this.currentBwUser = response;
      this.newUserFormGroup.patchValue({
        name: response.name,
        bw_user_profiles: response.bw_user_profiles,
        password: '123456',
        confirm: '123456',
      });
    }
  }

  get bwUserIsLinkedToCurrentCompanyGroup() {
    if (!this.currentBwUser) return false;
    const index = this.currentBwUser.bw_user_company_groups?.findIndex(
      (i: any) => i.company_group_id == this.currentCompanyGroup.id
    );
    return index >= 0;
  }

  async create() {
    if (this.loadingBwUser) return;

    if (!this.isEnableNewUser) {
      this.toastService.show({ color: 'warn', message: 'Usuário não tem permissão para criar' });
      return;
    }

    if (!this.currentBwUser || !this.bwUserIsLinkedToCurrentCompanyGroup) {
      const { email, name, password, bw_user_profiles, web_license_id } =
        this.newUserFormGroup.value;
      const body = new BwUser();
      this.saving = true;
      const response: any = await this.service
        .create({ ...body, email, name, password, bw_user_profiles })
        .finally(() => (this.saving = false));
      if (response?.data?.id) {
        this.currentBwUser = response.data;
      }

      // Falha ao vincular não pode barrar a navegação: o usuário já foi criado/vinculado ao
      // grupo, e o toast orienta a vincular depois na tela de licenças.
      if (this.canLinkLicense && web_license_id && this.currentBwUser?.id) {
        try {
          await this.licenseService.link(web_license_id, this.currentBwUser.id);
        } catch (error) {
          this.toastService.show({
            color: 'error',
            message: 'Usuário criado, mas houve um erro ao vincular o assento de licença',
          });
        }
      }
    }

    this.router.navigate([`/usuarios/${this.currentBwUser.id}`]);
    this.newUserModalVisible = false;
  }

  cancelCreate() {
    this.newUserModalVisible = false;
  }

  // --- Gerenciamento de senha pelo suporte (chamado 0000571410) ---

  selectedBwUser?: BwUser;
  changePasswordModalVisible: boolean = false;
  changePasswordSaving: boolean = false;

  resetLinkModalVisible: boolean = false;
  resetLinkLoading: boolean = false;
  resetLink?: string;
  resetLinkExpiresInHours?: number;
  loadingCopy: boolean = false;

  canManagePassword: boolean = false;

  private checkCanManagePassword(): boolean {
    if (utils.isSupport()) return true;

    const companyGroup = utils.getCompanyGroup();
    const hasDevReadWrite = utils.checkAccess(['Development'], true, true).result;

    return companyGroup?.alias === 'teorema' && hasDevReadWrite;
  }

  openChangePasswordModal(bwUser: BwUser) {
    this.selectedBwUser = bwUser;
    this.changePasswordFormGroup.reset({ password: '', confirm: '' });
    this.changePasswordModalVisible = false;
    setTimeout(() => {
      this.changePasswordModalVisible = true;
    });
  }

  async changePassword() {
    if (!this.selectedBwUser?.id) return;

    const { password, confirm } = this.changePasswordFormGroup.value;

    this.changePasswordSaving = true;
    await this.service
      .updatePassword(this.selectedBwUser.id, password, confirm)
      .then(() => {
        this.toastService.show({ color: 'success', message: 'A senha foi alterada' });
        this.changePasswordModalVisible = false;
        this.changePasswordFormGroup.reset({ password: '', confirm: '' });
      })
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: err?.error?.message || 'Houve um erro ao alterar a senha',
          });
      })
      .finally(() => {
        this.changePasswordSaving = false;
      });
  }

  async generatePasswordResetLink(bwUser: BwUser) {
    if (!bwUser?.id) return;

    this.selectedBwUser = bwUser;
    this.resetLink = undefined;
    this.resetLinkExpiresInHours = undefined;
    this.resetLinkLoading = true;

    const response = await this.service.generatePasswordResetLink(bwUser.id).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({
          color: 'error',
          message: err?.error?.message || 'Houve um erro ao gerar o link',
        });
      return null;
    });

    this.resetLinkLoading = false;

    if (response?.link) {
      this.resetLink = response.link;
      this.resetLinkExpiresInHours = response.expires_in_hours;
      this.resetLinkModalVisible = true;
    }
  }

  async copyResetLink() {
    if (!this.resetLink) return;

    this.loadingCopy = true;
    await utils
      .copyToClipboard(this.resetLink)
      .then(() => {
        this.toastService.show({ color: 'info', title: 'Pronto', message: 'Link copiado!' });
      })
      .catch(() => {
        this.toastService.show({ color: 'error', message: 'Erro ao copiar' });
      })
      .finally(() => {
        this.loadingCopy = false;
      });
  }
}
